import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import toast from "../helpers/toast";
import constants from "../../constants";

const MODULES = [
  { value: "order_medicine", label: "Order Medicine" },
  { value: "consult_doctor", label: "Consult Doctor" },
  { value: "diagnostics", label: "Diagnostics" },
];

const TYPES = [
  { value: "offers", label: "Offers" },
  { value: "banners", label: "Banners" },
];

const THEMES = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "blue", label: "Blue" },
];

const CARD_TYPES = [
  { value: "default", label: "Default" },
  { value: "highlight", label: "Highlight" },
  { value: "compact", label: "Compact" }
];

export default function Layout() {
  const [rows, setRows] = useState([
    { module: "", type: "", theme: "light", card_type: "default" }
  ]);
  const originalRowsRef = useRef([]);

  useEffect(() => {
    async function fetchLayouts() {
      try {
        const response = await fetch(constants.siteurl + "api/homelayout/gethomelayoutlist");
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          const loadedRows = result.data
            .sort((a, b) => a.position - b.position)
            .map(item => ({
              id: item.id,
              module: item.module_type || "",
              type: item.type || "",
              theme: item.theme || "light",
              card_type: item.card_type || "default"
            }));
          setRows(loadedRows.length ? loadedRows : [{ module: "", type: "", theme: "light", card_type: "default" }]);
          originalRowsRef.current = loadedRows; // Store original for comparison
        }
      } catch (err) {
        // Optionally handle error
      }
    }
    fetchLayouts();
  }, []);

  const handleRowChange = (idx, field, value) => {
    const newRows = [...rows];
    newRows[idx][field] = value;
    setRows(newRows);
  };

  const addRow = () => {
    setRows([...rows, { module: "", type: "", theme: "light", card_type: "default" }]);
  };

  const deleteRow = (idx) => {
    const newRows = rows.filter((_, i) => i !== idx);
    setRows(newRows.length ? newRows : [{ module: "", type: "", theme: "light", card_type: "default" }]);
  };

  const handleSave = async () => {
    // Compare with originalRowsRef to find changed records
    const changedRows = rows.filter((row, idx) => {
      const original = originalRowsRef.current[idx];
      // If new row (no id) or any field changed
      return (
        !row.id ||
        !original ||
        row.module !== original.module ||
        row.type !== original.type ||
        row.theme !== original.theme ||
        row.card_type !== original.card_type ||
        (idx + 1) !== original.position // position changed by drag/drop
      );
    });

    // Prepare payload
    const payload = changedRows.map((row, idx) => ({
      id: row.id,
      module_type: row.module,
      type: row.type,
      theme: row.theme,
      card_type: row.card_type,
      module_id: row.module === "order_medicine" ? 2 :
                 row.module === "consult_doctor" ? 1 :
                 row.module === "diagnostics" ? 3 : null,
      redirect_type: null,
      image_url: null,
      position: rows.indexOf(row) + 1,
      status: 1
    }));

    try {
      if (payload.length > 0) {
        const response = await fetch(
          constants.siteurl + "api/homelayout/",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
          }
        );
        const data = await response.json();
        if (data.success) {
          toast.success("Layouts updated!");
        } else {
          toast.warn("Failed to update layouts!");
        }
      } else {
        toast.info("No changes to save.");
      }
    } catch (err) {
      toast.error("Error updating layouts!");
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reorderedRows = Array.from(rows);
    const [removed] = reorderedRows.splice(result.source.index, 1);
    reorderedRows.splice(result.destination.index, 0, removed);
    setRows(reorderedRows);
  };

  return (
    <div className="container" style={{ maxWidth: 900, margin: "0px auto", paddingTop:"126px" }}>
      <div className="card" style={{ padding: "32px 24px", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h3 style={{ fontWeight: 600, margin: 0 }}>Layout Configuration</h3>
        </div>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="rows">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {rows.map((row, idx) => (
                  <Draggable key={idx} draggableId={`row-${idx}`} index={idx}>
                    {(provided) => (
                      <div
                        className="form-row align-items-center"
                        style={{ marginBottom: 16, background: "#f8f9fa", borderRadius: 8 }}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <div style={{ cursor: "grab", marginRight: 8 }} {...provided.dragHandleProps}>
                          <span style={{ fontSize: 20 }}>☰</span>
                        </div>
                        <div className="col">
                          <select
                            value={row.module}
                            onChange={e => handleRowChange(idx, "module", e.target.value)}
                            className="form-control"
                          >
                            <option value="">Select Module</option>
                            {MODULES.map(m => (
                              <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col">
                          <select
                            value={row.type}
                            onChange={e => handleRowChange(idx, "type", e.target.value)}
                            className="form-control"
                          >
                            <option value="">Select Type</option>
                            {TYPES.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col">
                          <select
                            value={row.theme}
                            onChange={e => handleRowChange(idx, "theme", e.target.value)}
                            className="form-control"
                          >
                            <option value="">Select Theme</option>
                            {THEMES.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col">
                          <select
                            value={row.card_type}
                            onChange={e => handleRowChange(idx, "card_type", e.target.value)}
                            className="form-control"
                          >
                            <option value="">Select Card Type</option>
                            {CARD_TYPES.map(c => (
                              <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-auto" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <button
                            type="button"
                            onClick={addRow}
                            className="btn btn-primary"
                            title="Add Row"
                            style={{ borderRadius: "50%", width: 36, height: 36, fontSize: 20, padding: 0 }}
                          >+</button>
                          {rows.length > 1 && (
                            <button
                              type="button"
                              onClick={() => deleteRow(idx)}
                              className="btn btn-danger"
                              title="Delete Row"
                              style={{ borderRadius: "50%", width: 36, height: 36, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
                            >
                              <span aria-hidden="true">&times;</span>
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <div style={{ marginTop: 32, textAlign: "right" }}>
          <button
            type="button"
            onClick={handleSave}
            className="btn btn-success"
            style={{ fontWeight: 600, padding: "10px 32px", fontSize: 16 }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}